import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import MemberCard from "@/components/MemberCard";
import { memberCategories } from "@/data/members";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Users, Lightbulb, Handshake, Filter, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

type Member = Tables<"members">;

const Members = () => {
  const location = useLocation();
  
  // Determine chapter based on URL path (e.g., /washington/members or /union/members)
  const pathSegments = location.pathname.split('/');
  const chapterSlug = pathSegments[1]; // 'washington' or 'union'
  const isUnionChapter = chapterSlug === 'union';
  const chapterFilter = isUnionChapter ? 'union' : 'washington';
  const chapterName = isUnionChapter ? 'Union' : 'Washington';

  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    fetchMembers();

    // Set up real-time subscription
    const channel = supabase.channel('members-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'members'
    }, () => {
      fetchMembers();
    }).subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chapterFilter]);

  const fetchMembers = async () => {
    try {
      // Include members whose primary chapter matches OR whose additional_chapters
      // contains this chapter (dual-chapter members appear in both directories).
      const { data, error } = await supabase
        .from('members')
        .select('*')
        .or(`chapter.eq.${chapterFilter},additional_chapters.cs.{${chapterFilter}}`)
        .eq('is_hidden', false)
        .order('name');

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(member => {
    const matchesCategory = selectedCategory === "All" || member.category === selectedCategory;
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase()) || member.company.toLowerCase().includes(searchQuery.toLowerCase()) || (member.description || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-[10px] pt-[120px] pb-0">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 animate-fade-in">
              Welcome to the {chapterName} Chapter!
            </h1>
            <p className="text-lg text-muted-foreground animate-fade-in" style={{
            animationDelay: "0.1s"
          }}>
              Discover the power of connections in our vibrant networking group. Member profiles showcase the diverse talents and services within our community, fostering collaboration and growth.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            <div className="text-center p-6 bg-card rounded-lg animate-fade-in" style={{
            animationDelay: "0.2s"
          }}>
              <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Connect with Members</h3>
              <p className="text-sm text-muted-foreground">
                Explore profiles of local businesses and professionals who are eager to collaborate.
              </p>
            </div>

            <div className="text-center p-6 bg-card rounded-lg animate-fade-in" style={{
            animationDelay: "0.3s"
          }}>
              <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Gain Valuable Insights</h3>
              <p className="text-sm text-muted-foreground">
                Our members share their expertise and insights, providing valuable knowledge to enhance your business.
              </p>
            </div>

            <div className="text-center p-6 bg-card rounded-lg animate-fade-in" style={{
            animationDelay: "0.4s"
          }}>
              <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Handshake className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Collaborate and Thrive</h3>
              <p className="text-sm text-muted-foreground">
                Join like-minded professionals and create opportunities that drive success for everyone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-4 lg:py-8 border-b bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col gap-4">
            {/* Search */}
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input type="text" placeholder="Search members, companies, or services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" />
            </div>

            {/* Category Filters - Desktop: Always visible, Mobile: Collapsible */}
            <div className="lg:hidden">
              <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Filter by Category: {selectedCategory}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <div className="flex flex-wrap gap-2">
                    {memberCategories.map(category => <Button key={category} variant={selectedCategory === category ? "default" : "outline"} size="sm" onClick={() => {
                    setSelectedCategory(category);
                    setFiltersOpen(false);
                  }} className="whitespace-nowrap">
                        {category}
                      </Button>)}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Desktop Category Filters */}
            <div className="hidden lg:flex flex-wrap gap-2 justify-center">
              {memberCategories.map(category => <Button key={category} variant={selectedCategory === category ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(category)} className="whitespace-nowrap">
                  {category}
                </Button>)}
            </div>

            {/* Results Count */}
            <div className="text-center lg:text-left">
              <p className="text-sm text-muted-foreground">
                Showing <span className="font-semibold text-foreground">{filteredMembers.length}</span> of{" "}
                <span className="font-semibold text-foreground">{members.length}</span> members
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Members Grid */}
      <section className="flex-1 py-12 bg-secondary/20">
        <div className="container mx-auto px-4">
          {loading ? <div className="text-center py-16">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="text-muted-foreground mt-4">Loading members...</p>
            </div> : filteredMembers.length > 0 ? <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMembers.map(member => <div key={member.id} className="animate-fade-in">
                  <MemberCard member={member} />
                </div>)}
            </div> : <div className="text-center py-16">
              <div className="bg-muted/50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No members found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search or filter criteria
              </p>
              <Button variant="outline" onClick={() => {
            setSearchQuery("");
            setSelectedCategory("All");
          }}>
                Clear Filters
              </Button>
            </div>}
        </div>
      </section>

      <Footer />
    </div>;
};
export default Members;